/* eslint-disable sonarjs/no-duplicate-string */

import {
  Text, Input, InputDescription, InputGroup, InputLabel,
  PhoneNumberInput,
  Button
} from '@huspy/briks-web';
import { FileWarning } from '@huspy/briks-icons';
import { theme } from '@huspy/forge';
import { validators } from '@huspy/forge/shared';
import {
  Box, Flex, SimpleGrid,
  Skeleton,
  rem
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import useGetBrokerProfile from '@modules/core/hooks/queries/useGetBrokerProfile';
import { useGlobalStore } from '@modules/core/store';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useEffect } from 'react';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import useUpdateBroker from '@modules/core/hooks/mutations/useUpdateBroker';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import Card from '../../components/Card';

const { textFieldValidator } = validators;

const schema = z.object({
  first_name: textFieldValidator,
  last_name: textFieldValidator,
  email: textFieldValidator,
  phone_number: textFieldValidator.optional().or(z.literal('')),
});

const AccountDetails = () => {
  const { t } = useTranslation();
  const { user } = useGlobalStore();
  const { data, isLoading: isBrokerProfileLoading } = useGetBrokerProfile(user?.id);
  const { mutateAsync: updateBroker, isPending: isUpdateBrokerPending } = useUpdateBroker();
  const { isFeatureActive } = useFeatureFlags();
  const isPhoneNumberEditable = isFeatureActive('account.phoneNumberEditable');

  const isLoading = !user || isBrokerProfileLoading;

  const form = useForm<{
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string | undefined;
  }>({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: undefined,
    },
    validateInputOnChange: true,
    validate: zodResolver(schema),
  });

  useEffect(() => {
    if (data) {
      const values = {
        first_name: data.first_name ?? '',
        last_name: data.last_name ?? '',
        email: data.email ?? '',
        phone_number: data.phone_number ?? '',
      };

      form.setInitialValues(values);
      form.setValues(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onFormSubmit = async () => {
    await updateBroker({
      userId: user?.id as string,
      phone_number: form.values.phone_number as string,
    });
  };

  if (isLoading) {
    return (
      <Flex gap={ theme.spacing.lg } direction='column'>
        <Skeleton radius={ theme.radius.md } h={ rem(295) } />
        <Skeleton radius={ theme.radius.md } h={ rem(295) } />
      </Flex>
    );
  }

  return (
    <Card title={ t('account.personalInfo') }>
      <Flex pb={ theme.spacing.xl } direction='row' align='center'>
        <Box pr={ theme.spacing.xs }>
          <FileWarning />
        </Box>
        <Text size='md' weight='medium' color='text-secondary'>
          {t('account.contactToEdit')}
        </Text>
      </Flex>

      <form onSubmit={ form.onSubmit(onFormSubmit) }>
        <SimpleGrid
          spacing={ theme.spacing.lg }
          verticalSpacing={ theme.spacing.xl }
          cols={ 2 }
        >
          <InputGroup isError={ !!form.getInputProps('first_name')?.error }>
            <InputLabel>{ t('common.forms.firstName.label') }</InputLabel>
            <Input
              name='first_name'
              placeholder={ t('common.forms.firstName.placeholder') }
              data-test='first-name'
              disabled
              { ...form.getInputProps('first_name') }
            />
            <InputDescription>{form.getInputProps('first_name')?.error ?? ''}</InputDescription>
          </InputGroup>

          <InputGroup isError={ !!form.getInputProps('last_name')?.error }>
            <InputLabel>{ t('common.forms.lastName.label') }</InputLabel>
            <Input
              name='last_name'
              placeholder={ t('common.forms.lastName.placeholder') }
              data-test='last-name'
              disabled
              { ...form.getInputProps('last_name') }
            />
            <InputDescription>{form.getInputProps('last_name')?.error ?? ''}</InputDescription>
          </InputGroup>

          <InputGroup isError={ !!form.getInputProps('email')?.error }>
            <InputLabel>{ t('common.forms.email.label') }</InputLabel>
            <Input
              name='email'
              placeholder={ t('common.forms.email.placeholder') }
              data-test='email'
              disabled
              { ...form.getInputProps('email') }
            />
            <InputDescription>{form.getInputProps('email')?.error ?? ''}</InputDescription>
          </InputGroup>

          {form.values.phone_number !== undefined && (
            <InputGroup isError={ !!form.errors.phone_number }>
              <InputLabel>{ t('common.forms.mobile.label') }</InputLabel>
              <PhoneNumberInput
                disabled={ !isPhoneNumberEditable || isUpdateBrokerPending }
                name='phone_number'
                placeholder={ t('common.forms.mobile.placeholder') }
                data-test='phone-number'
                defaultCountry={ IS_SPAIN_ENV ? 'es' : 'ae' }
                value={ form.values.phone_number ?? '' }
                onChange={ (e) => {
                  const newValue = e.inputValue.length === 0 ? '' : e.phone;
                  form.setFieldValue('phone_number', newValue);
                  form.clearFieldError('phone_number');
                } }
              />
              <InputDescription>{form.errors.phone_number ?? ''}</InputDescription>
            </InputGroup>
          )}
        </SimpleGrid>
        { isPhoneNumberEditable && (
          <Flex justify='flex-end' mt={ theme.spacing.xl }>
            <Button type='submit' disabled={ isUpdateBrokerPending }>
              { t('account.save') }
            </Button>
          </Flex>
        )}
      </form>
    </Card>
  );
};

export default AccountDetails;
